/*
 * Purpose: Command center — live runs, flow survey, stats, compose resume
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  Hammer,
  Layers,
  Plus,
  Sparkles,
  LayoutTemplate,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';
import { Card } from '@/components/ui/card';
import { LiveRunningFlowsPanel } from '@/components/dashboard/LiveRunningFlowsPanel';
import { FlowTemplatesSpotlight } from '@/components/dashboard/FlowTemplatesSpotlight';
import { LearningFlowsPanel } from '@/components/dashboard/LearningFlowsPanel';
import {
  ComposeInProgressBanner,
  DashboardStatGrid,
  FlowSurveySection,
} from '@/components/dashboard/FlowSurveySection';
import { surveyFlows, isComposeSessionActive } from '@/lib/flowSurvey';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { APP_ROUTES } from '@/lib/appRoutes';
import { findBuilderNavItem } from '@/lib/productActivities';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useExecutionRuntimeStore } from '@/stores/executionRuntimeStore';
import { useAutoBlocksStore } from '@/stores/autoBlocksStore';
import { useLogStore } from '@/stores/logStore';
import { PRODUCT_TERMS } from '@/lib/productTerms';

const MY_FLOWS = findBuilderNavItem('flows');

export function CommandCenterDashboard() {
  const workflows = useWorkflowStore((s) => s.workflows);
  const running = useExecutionRuntimeStore((s) => s.running);
  const runningIds = useMemo(() => running.map((r) => r.workflowId), [running]);
  const phase = useAutoBlocksStore((s) => s.phase);
  const outcomeText = useAutoBlocksStore((s) => s.outcomeText);
  const logs = useLogStore((s) => s.logs);

  const survey = useMemo(() => surveyFlows(workflows, runningIds), [workflows, runningIds]);
  const composeActive = isComposeSessionActive(phase, outcomeText);
  const recentLogs = logs.slice(0, 6);

  const statCards = [
    {
      label: 'Live now',
      value: survey.running,
      icon: Activity,
      accent: 'text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]',
    },
    {
      label: 'Ready to run',
      value: survey.ready,
      icon: CheckCircle2,
      accent: 'text-[hsl(var(--ab-green-sea))]',
    },
    {
      label: 'Still building',
      value: survey.building,
      icon: Hammer,
      accent: 'text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Total flows',
      value: survey.total,
      icon: Layers,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Command center
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mt-1">Your flows at a glance</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Live {PRODUCT_TERMS.mission.toLowerCase()} runs up front — then ready flows, work in progress, and compose sessions.
          </p>
        </div>
        <ActionButtonGroup
          context="CommandCenterDashboard hero"
          actions={[
            {
              tier: 'primary',
              label: 'New outcome',
              href: '/compose',
              icon: <Sparkles className="h-4 w-4 mr-1.5" />,
            },
            {
              tier: 'secondary',
              label: PRODUCT_TERMS.flowTemplates,
              href: APP_ROUTES.flowTemplates,
              icon: <LayoutTemplate className="h-4 w-4 mr-1.5" />,
            },
            {
              tier: 'ghost',
              label: MY_FLOWS?.label ?? 'My flows',
              href: REMEDIATION.flows,
              icon: <Plus className="h-4 w-4 mr-1.5" />,
            },
          ]}
        />
      </div>

      <section className="space-y-3" aria-labelledby="live-runs-heading">
        <div className="flex items-center justify-between gap-2">
          <h2 id="live-runs-heading" className="text-lg font-semibold text-foreground flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--ab-green-glow))] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--ab-green-core))]" />
            </span>
            Live running flows
          </h2>
          {survey.running > 0 ? (
            <span className="text-xs text-muted-foreground">{survey.running} active</span>
          ) : null}
        </div>
        <LiveRunningFlowsPanel />
      </section>

      {composeActive ? <ComposeInProgressBanner outcomeText={outcomeText} phase={phase} /> : null}

      <FlowTemplatesSpotlight />

      <LearningFlowsPanel />

      <DashboardStatGrid stats={statCards} />

      <div className="grid gap-2 sm:grid-cols-3 text-center sm:text-left">
        <Card className="p-3 sm:p-4 bg-muted/20 border-border/60">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Active this week</p>
          <p className="text-lg font-semibold tabular-nums mt-1">{survey.activeRecently}</p>
        </Card>
        <Card className="p-3 sm:p-4 bg-muted/20 border-border/60">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Blocks across flows</p>
          <p className="text-lg font-semibold tabular-nums mt-1">{survey.totalNodes}</p>
        </Card>
        <Card className="p-3 sm:p-4 bg-muted/20 border-border/60">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Links wired</p>
          <p className="text-lg font-semibold tabular-nums mt-1">{survey.totalLinks}</p>
        </Card>
      </div>

      {survey.buildingItems.length > 0 ? (
        <FlowSurveySection
          title="Still building"
          description="Flows missing blocks, wiring, or validation — pick up where you left off."
          items={survey.buildingItems.filter((i) => i.status !== 'running')}
          emptyMessage=""
        />
      ) : null}

      <FlowSurveySection
        title="Ready to run"
        description="Validated flows — open the Mission map to start or schedule."
        items={survey.readyItems}
        emptyMessage="No complete flows yet. Compose an outcome or finish wiring in the builder."
        emptyAction={{ label: `Open ${PRODUCT_TERMS.autoBlockFlowComposer}`, href: '/compose' }}
      />

      {recentLogs.length > 0 ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Recent activity</h2>
          </div>
          <Card className="divide-y divide-border overflow-hidden">
            {recentLogs.map((log) => (
              <div key={log.id} className="px-4 py-3 flex items-start gap-3 text-sm">
                <span
                  className={`mt-1 h-2 w-2 rounded-full shrink-0 ${
                    log.level === 'error'
                      ? 'bg-destructive'
                      : log.level === 'success'
                        ? 'bg-[hsl(var(--ab-green-core))]'
                        : log.level === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-muted-foreground'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate">{log.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {log.timestamp.toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </Card>
          <Button asChild variant="ghost" size="sm" className="text-xs">
            <Link to={REMEDIATION.missionLogs}>View all logs</Link>
          </Button>
        </section>
      ) : null}
    </div>
  );
}

export default CommandCenterDashboard;
